import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useAdmin } from '../hooks/useAdmin';
import ConnectButton from '../components/common/ConnectButton';
import Loader from '../components/common/Loader';
import AdminStats from '../components/admin/AdminStats';
import ContractStatus from '../components/admin/ContractStatus';
import UserLookup from '../components/admin/UserLookup';
import AdminUserOps from '../components/admin/AdminUserOps';
import AllUsersList from '../components/admin/AllUsersList';
import EmergencyWithdraw from '../components/admin/EmergencyWithdraw';
import OwnerControls from '../components/admin/OwnerControls';
import RouterFlags from '../components/admin/RouterFlags';
import RouterPool from '../components/admin/RouterPool';
import WalletUpdater from '../components/admin/WalletUpdater';

const Admin = () => {
  const { connected } = useWallet();
  const { isOwner, isAdmin, isElevated, hasLoaded } = useAdmin();
  const [tab, setTab] = useState('users');

  if (!connected) {
    return (
      <div className="main-content">
        <div className="connect-prompt">
          <div className="connect-prompt-icon">🔗</div>
          <h2>Connect Your Wallet</h2>
          <div className="mt-lg"><ConnectButton /></div>
        </div>
      </div>
    );
  }

  if (!hasLoaded) return <Loader text="Verifying Access" />;
  if (!isElevated) return <Navigate to="/" replace />;

  // Tabs available based on role
  const tabs = [
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'manage', label: 'Manage', icon: '⚙' },
    { id: 'stats', label: 'Stats', icon: '📊' }
  ];
  if (isOwner) {
    tabs.push({ id: 'contract', label: 'Contracts', icon: '🔗' });
    tabs.push({ id: 'router', label: 'Router', icon: '⚡' });
    tabs.push({ id: 'wallets', label: 'Wallets', icon: '💼' });
    tabs.push({ id: 'ownership', label: 'Ownership', icon: '👑' });
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <h1 className="page-title">{isOwner ? 'Owner Panel' : 'Admin Panel'}</h1>
        <p className="page-subtitle">
          Signed in as{' '}
          <span className={isOwner ? 'chip chip-cyan' : 'chip chip-violet'} style={{ marginLeft: 6 }}>
            {isOwner ? '👑 OWNER' : '🛡 ADMIN'}
          </span>
        </p>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={'admin-tab ' + (tab === t.id ? 'active' : '')}
            onClick={() => setTab(t.id)}
          >
            <span className="admin-tab-icon">{t.icon}</span>
            <span className="admin-tab-label">{t.label}</span>
          </button>
        ))}
      </div>

      {/* USERS tab — look up + register/activate */}
      {tab === 'users' && (
        <>
          <UserLookup />
          <div className="section-heading">
            <span className="section-heading-bar"></span>
            Register / Activate Without Payment
          </div>
          <AdminUserOps />
        </>
      )}

      {/* MANAGE tab — all users list + emergency withdraw (both roles) */}
      {tab === 'manage' && (
        <>
          <AllUsersList />
          <div className="section-heading">
            <span className="section-heading-bar"></span>
            Emergency Controls
          </div>
          <EmergencyWithdraw />
        </>
      )}

      {/* STATS tab */}
      {tab === 'stats' && (
        <>
          <AdminStats />
          {isOwner && (
            <>
              <div className="section-heading">
                <span className="section-heading-bar"></span>
                Router Pool Balance
              </div>
              <RouterPool />
            </>
          )}
        </>
      )}

      {/* CONTRACTS tab — owner only */}
      {tab === 'contract' && isOwner && <ContractStatus />}

      {/* ROUTER tab — owner only */}
      {tab === 'router' && isOwner && (
        <div className="grid grid-2">
          <RouterFlags />
          <RouterPool />
        </div>
      )}

      {/* WALLETS tab — owner only */}
      {tab === 'wallets' && isOwner && <WalletUpdater />}

      {/* OWNERSHIP tab — owner only — change admin on both contracts */}
      {tab === 'ownership' && isOwner && <OwnerControls />}

      {/* Admin-only fallback (safety) */}
      {!isOwner && isAdmin && !['users', 'manage', 'stats'].includes(tab) && (
        <div className="empty-state">
          <div className="empty-state-icon">🔒</div>
          <div>This section is owner-only.</div>
        </div>
      )}
    </div>
  );
};

export default Admin;
